import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, GraduationCap, Sparkles, Wallet, Users, Zap, ArrowUpRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

const UNIVERSES = [
  { key: "work", icon: Briefcase, to: "/work", color: "bg-[#C86A51]", countKeys: ["cvs", "cover_letters", "translations"] },
  { key: "learn", icon: GraduationCap, to: "/learn", color: "bg-[#4C86B8]", countKeys: ["quizzes", "flashcards", "summaries"] },
  { key: "create", icon: Sparkles, to: "/create", color: "bg-[#8E6BB8]", countKeys: ["content"] },
  { key: "money", icon: Wallet, to: "/money", color: "bg-[#3E9B87]", countKeys: ["budgets"] },
  { key: "community", icon: Users, to: "/community", color: "bg-[#E6A840]", countKeys: ["posts"] },
];

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const name = user?.profile?.full_name?.split(" ")[0] || user?.email?.split("@")[0] || "";
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "short" }) : "";

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t("dashboard.welcome")}, {name} 👋</h1>
          <p className="mt-1 text-muted-foreground">{t("dashboard.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border bg-card px-4 py-3">
          <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary"><Zap className="h-5 w-5" /></div>
          <div>
            {data ? (
              <div className="font-display font-bold text-lg leading-none" data-testid="dashboard-credits">{data.credits}</div>
            ) : (
              <div className="h-5 w-10 rounded bg-muted-foreground/20 animate-pulse" data-testid="dashboard-credits-loading" />
            )}
            <div className="text-xs text-muted-foreground">{t("dashboard.creditsLeft")}</div>
          </div>
          <Button size="sm" variant="outline" className="rounded-full ml-2" onClick={() => navigate("/pricing")} data-testid="dashboard-upgrade">{t("dashboard.upgrade")}</Button>
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-semibold mb-4">{t("dashboard.quickAccess")}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {UNIVERSES.map((u, i) => {
            const Icon = u.icon;
            const total = u.countKeys.reduce((acc, k) => acc + (data?.counts?.[k] || 0), 0);
            return (
              <motion.button key={u.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                onClick={() => navigate(u.to)} data-testid={`dash-universe-${u.key}`}
                className="group text-left rounded-2xl border bg-card p-6 hover:-translate-y-1 transition-transform">
                <div className="flex items-start justify-between">
                  <div className={`h-11 w-11 rounded-xl ${u.color} flex items-center justify-center text-white`}><Icon className="h-5 w-5" /></div>
                  <ArrowUpRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{t(`nav.${u.key}`)}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">{t(`universes.${u.key}`)}</p>
                <p className="text-xs text-primary mt-2 font-medium">{total} {t("common.all").toLowerCase()}</p>
              </motion.button>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Clock className="h-4 w-4" /> {t("dashboard.recentActivity")}</h2>
        <div className="rounded-2xl border bg-card divide-y">
          {(!data?.recent_activity || data.recent_activity.length === 0) && (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="no-activity">{t("dashboard.noActivity")}</div>
          )}
          {data?.recent_activity?.map((a, i) => (
            <div key={i} className="flex items-center justify-between p-4" data-testid="activity-item">
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-wider text-primary font-semibold w-20">{t(`nav.${a.module}`)}</span>
                <span className="text-sm">{a.label}</span>
              </div>
              <span className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
